import { Injectable } from '@angular/core';
import { ContentService } from '@blk/cwp-ng-framework';
import { ErrorCode } from '@blk/wealth-web-endpoint';
import { TealiumAnalyticsService } from '@ddt-web/analytics';
import { ERROR_SEVERITY, ErrorDCR, PacErrorDCR, PAErrorDCR } from '@ddt-web/config';
import * as get from 'lodash/get';
import * as toPairsIn from 'lodash/toPairsIn';

import { WwePortfolioAnalysisPartialError } from '../../models';
import { ErrorObject } from '../../models/error-object';
import { PopupDialogObject } from '../../models/popup-dialog-object';
import { WweErrorUtil } from '../../utils/wwe-error.util';
import { AbstractErrorHandlerService } from '../abstract-error-handler.service';
import { ErrorHelperService } from '../error-helper.service';
import { ErrorPopupService } from '../error-popup.service';

@Injectable()
export class WwePortfolioAnalysisErrorHandlerService extends AbstractErrorHandlerService {
	private flagErrorConfigs = ['details', 'risk', 'performance'];
	private readonly errorTypes = {
		SECURITIES_NOT_FOUND: 'SECURITIES_NOT_FOUND'
	};

	constructor(
		protected contentService: ContentService,
		protected errorPopupService: ErrorPopupService,
		protected tealiumAnalyticsService: TealiumAnalyticsService,
		protected errorHelperService: ErrorHelperService
	) {
		super(
			contentService,
			errorPopupService,
			tealiumAnalyticsService,
			errorHelperService
		);
	}

	public handleError(error): void {
		const configName = get(error, 'configName', '');
		if (error instanceof ErrorObject && this.flagErrorConfigs.includes(configName)) {
			// Setup scope variables
			const errorObject = error.responseJson;
			const isPartialError = errorObject instanceof WwePortfolioAnalysisPartialError;

			// Check if security error
			const isSecurityError = errorObject?.errorCode === this.errorTypes.SECURITIES_NOT_FOUND;

			// Set error severity
			this.errorSeverity = isPartialError || isSecurityError ? ERROR_SEVERITY.ERROR : ERROR_SEVERITY.CRITICAL;
			this.httpRequestId = error?.responseJson?.headers?.get('x-request-id');

			let dialogErrorMessage: PopupDialogObject;

			if (isPartialError) {
				const errorCodes = errorObject.errorCodes;
				const errorCodesList = toPairsIn(errorCodes);

				// Log each error to console
				errorCodesList.forEach(error => this.logErrorsToConsole(error[0], error[1], configName));

				// Build partial error dialog
				dialogErrorMessage = this.handlePartialErrorDialogErrorMessage(
					this.contentService.get('PAC_HOLDING_ERROR_DESCRIPTION', PacErrorDCR.PAC_HOLDING_ERROR_DESCRIPTION),
					get(errorObject, 'portfolioNames', []),
					get(errorCodesList, '[0][0]', ''));
			} else if (isSecurityError) {
				console.error('There was a security error while retrieving data for config: ' + configName, errorObject);

				const portfolioIds = this.errorHelperService.getRouterUrlParam('portfolios').split(',').filter(item => item !== 'list');
				dialogErrorMessage = this.handlePartialErrorDialogErrorMessage(
					this.contentService.get('PAC_SECURITY_ERROR_DESCRIPTION', PacErrorDCR.PAC_SECURITY_ERROR_DESCRIPTION),
					[],
					portfolioIds.length ? portfolioIds[0] : '');

			} else {
				console.error('There was an error while retrieving data for config: ' + configName, errorObject);
				// Build default error object
				dialogErrorMessage = this.handleDialogErrorMessage();
			}

			this.handleErrorTracking('unabletoprocessrequest', this.errorSeverity);
			dialogErrorMessage.trackMsgPrefix = this.errorSeverity;
			this.errorPopupService.openResponseMessageDialog(dialogErrorMessage, true);
		} else {
			this.nextHandler.handleError(error);
		}
	}

	private logErrorsToConsole(errorKey: string, error: ErrorCode, configName: string): void {
		const message = error.description ? error.description : this.contentService.get('PORTFOLIO_ANALYSIS_COMMON_ERROR', ErrorDCR.PORTFOLIO_ANALYSIS_COMMON_ERROR);
		console.error(`There was an error while retrieving data for config: ${configName} and portfolioId: ${errorKey}`, `${error.code}: ${message}`);
	}

	private handlePartialErrorDialogErrorMessage(description: string, portfolioNames: string[], firstFailedPortfolioId: string): PopupDialogObject {
		const dialogErrorMessage = this.errorHelperService.buildPopupDialogObject(description, null, false, this.buildErrorMessage());
		dialogErrorMessage.disableClose = false;
		dialogErrorMessage.listings = portfolioNames.map(name => {
			return { iconType: 'times', description: name };
		});

		dialogErrorMessage.actions = [
			WweErrorUtil.buildViewPortfolioInEditorAction(
				firstFailedPortfolioId,
				this.contentService.get('ERROR_REVIEW_PORTFOLIO_HOLDINGS_LABEL', ErrorDCR.ERROR_REVIEW_PORTFOLIO_HOLDINGS_LABEL)
			)
		];
		return dialogErrorMessage;
	}

	private handleDialogErrorMessage(): PopupDialogObject {
		const description = this.contentService.get('PORTFOLIO_ANALYSIS_COMMON_ERROR', ErrorDCR.PORTFOLIO_ANALYSIS_COMMON_ERROR);
		const label = this.contentService.get('PA_RETRY_MESSAGE', PAErrorDCR.PA_RETRY_LABEL);

		return this.errorHelperService.buildPopupDialogObject(description, label, true, this.buildErrorMessage());
	}
}
